import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout, LegalSection, useIsJa } from '@/components/layout/LegalLayout';

interface SectionContent {
  h: string;
  body: ReactNode;
}

interface Section {
  id: string;
  en: SectionContent;
  ja: SectionContent;
}

const SECTIONS: Section[] = [
  {
    id: 'intro',
    en: {
      h: 'Overview',
      body: (
        <p>
          This Privacy Policy explains how CadencEZ Inc. ("CadencEZ", "we"), a Delaware,
          USA corporation, collects, uses, and protects personal data when you visit our
          website or use our financial planning &amp; analysis service (the "Service"). It
          is written to meet the requirements of the EU General Data Protection Regulation
          (GDPR), the UK GDPR, the California Consumer Privacy Act as amended (CCPA/CPRA),
          and Japan's Act on the Protection of Personal Information (APPI).
        </p>
      ),
    },
    ja: {
      h: '概要',
      body: (
        <p>
          本プライバシーポリシーは、米国デラウェア州法人である CadencEZ Inc.（以下「CadencEZ」「当社」）が、
          当社ウェブサイトのご利用時または当社の財務計画・分析サービス（以下「本サービス」）のご利用時に、
          個人情報をどのように収集・利用・保護するかを説明するものです。本ポリシーは、EU 一般データ保護規則（GDPR）、
          英国 GDPR、改正カリフォルニア州消費者プライバシー法（CCPA/CPRA）、および日本の個人情報保護法（APPI）の
          要件を満たすよう作成されています。
        </p>
      ),
    },
  },
  {
    id: 'who',
    en: {
      h: '1. Who we are',
      body: (
        <p>
          CadencEZ Inc. is the controller of personal data processed about visitors to our
          website and the administrators of customer accounts. For data that our customers
          upload into the Service about their own business (financial data, employee names
          in headcount plans, and similar), the customer is the controller and CadencEZ is
          the processor — see our <Link to="/dpa">Data Processing Agreement</Link>.
        </p>
      ),
    },
    ja: {
      h: '1. 当社について',
      body: (
        <p>
          CadencEZ Inc. は、当社ウェブサイトの訪問者および顧客アカウントの管理者に関して処理される個人情報の
          管理者（コントローラー）です。顧客が自社の事業に関して本サービスにアップロードするデータ（財務データ、
          要員計画における従業員氏名など）については、顧客が管理者であり、CadencEZ は処理者（プロセッサー）となります。
          詳細は<Link to="/dpa">データ処理契約（DPA）</Link>をご覧ください。
        </p>
      ),
    },
  },
  {
    id: 'collect',
    en: {
      h: '2. What we collect',
      body: (
        <ul>
          <li><strong>Account data:</strong> name, work email, company, and authentication identifiers (including data from Google, SAML, or OIDC sign-in if you use it).</li>
          <li><strong>Customer content:</strong> the financial and operational data you upload or connect (e.g. ledger actuals, budgets, headcount plans). We process this on your behalf.</li>
          <li><strong>Usage &amp; device data:</strong> IP address, browser type, pages viewed, and audit-log events (who did what, when) needed to secure and operate the Service.</li>
          <li><strong>Billing data:</strong> handled by Stripe; we store a Stripe customer reference, not card numbers.</li>
          <li><strong>Preferences:</strong> language and currency preferences stored to remember your choices.</li>
        </ul>
      ),
    },
    ja: {
      h: '2. 収集する情報',
      body: (
        <ul>
          <li><strong>アカウント情報：</strong>氏名、業務用メールアドレス、会社名、および認証識別子（Google、SAML、OIDC によるサインインをご利用の場合は当該データを含みます）。</li>
          <li><strong>顧客コンテンツ：</strong>お客様がアップロードまたは連携する財務・業務データ（元帳実績、予算、要員計画など）。当社はこれをお客様に代わって処理します。</li>
          <li><strong>利用・デバイス情報：</strong>本サービスの安全な運用に必要な IP アドレス、ブラウザの種類、閲覧ページ、および監査ログイベント（誰が・いつ・何をしたか）。</li>
          <li><strong>請求情報：</strong>Stripe が取り扱います。当社はカード番号ではなく Stripe の顧客参照情報を保存します。</li>
          <li><strong>設定情報：</strong>お客様の選択を記憶するために保存する言語および通貨の設定。</li>
        </ul>
      ),
    },
  },
  {
    id: 'use',
    en: {
      h: '3. How we use it',
      body: (
        <p>We use personal data to provide and secure the Service, authenticate users, process billing, respond to support requests, comply with legal obligations, and improve the product. We do not sell personal data.</p>
      ),
    },
    ja: {
      h: '3. 利用目的',
      body: (
        <p>当社は、本サービスの提供および安全確保、ユーザー認証、請求処理、サポート対応、法的義務の遵守、ならびに製品の改善のために個人情報を利用します。当社は個人情報を販売しません。</p>
      ),
    },
  },
  {
    id: 'legal-bases',
    en: {
      h: '4. Legal bases (GDPR/UK GDPR)',
      body: (
        <p>We rely on: performance of a contract (to provide the Service), legitimate interests (to secure and improve the Service), consent (where required, e.g. certain cookies), and legal obligation (e.g. tax records).</p>
      ),
    },
    ja: {
      h: '4. 法的根拠（GDPR／英国 GDPR）',
      body: (
        <p>当社は、契約の履行（本サービスの提供のため）、正当な利益（本サービスの安全確保および改善のため）、同意（特定のクッキーなど、必要とされる場合）、および法的義務（税務記録など）を法的根拠とします。</p>
      ),
    },
  },
  {
    id: 'cookies',
    en: {
      h: '5. Cookies & similar technologies',
      body: (
        <>
          <p>
            CadencEZ uses only essential cookies, session cookies, and browser local storage.
            These support:
          </p>
          <ul>
            <li><strong>Authentication &amp; session:</strong> keeping you signed in and protecting your session.</li>
            <li><strong>Preferences:</strong> remembering your language and currency choices.</li>
          </ul>
          <p>
            We do <strong>not</strong> use third-party advertising cookies or cross-site
            tracking cookies. You can clear or block cookies and local storage through your
            browser settings; doing so may affect sign-in and other core functionality of the
            Service.
          </p>
        </>
      ),
    },
    ja: {
      h: '5. クッキーおよび類似技術',
      body: (
        <>
          <p>
            CadencEZ は、必須クッキー、セッションクッキー、およびブラウザのローカルストレージのみを使用します。
            これらは以下を目的とします。
          </p>
          <ul>
            <li><strong>認証・セッション：</strong>サインイン状態の維持およびセッションの保護。</li>
            <li><strong>設定：</strong>言語および通貨の選択の記憶。</li>
          </ul>
          <p>
            当社は、第三者の広告クッキーやサイト横断的なトラッキングクッキーを<strong>使用しません</strong>。
            クッキーおよびローカルストレージは、ブラウザの設定から消去または拒否できますが、その場合、サインインや
            本サービスのその他の中核的機能に影響が生じることがあります。
          </p>
        </>
      ),
    },
  },
  {
    id: 'subprocessors',
    en: {
      h: '6. Subprocessors & where data lives',
      body: (
        <>
          <p>
            We use a small set of vetted subprocessors to run the Service. The current list
            and primary hosting region are also on our <Link to="/security">Security &amp; subprocessors</Link> page:
          </p>
          <ul>
            <li><strong>Hetzner</strong> — cloud hosting &amp; compute (Germany, EU).</li>
            <li><strong>Cloudflare R2</strong> — object storage for uploads, exports, and backups (EU / global edge).</li>
            <li><strong>Stripe</strong> — payments &amp; subscription billing; no card numbers stored by us (US / EU).</li>
            <li><strong>Amazon SES</strong> — transactional email (EU).</li>
            <li><strong>OpenRouter</strong> — AI model gateway for CadencEZ AI (US).</li>
            <li><strong>Google</strong> — optional Google sign-in (US).</li>
          </ul>
          <p>
            Customer content is hosted in the European Union (Germany) by default. An
            Enterprise option for Japan-resident data is available. Where data is transferred
            outside your region, we rely on Standard Contractual Clauses or an equivalent
            transfer mechanism.
          </p>
        </>
      ),
    },
    ja: {
      h: '6. 委託先（サブプロセッサー）とデータの保管場所',
      body: (
        <>
          <p>
            当社は、本サービスの運用のために、審査済みの少数の委託先（サブプロセッサー）を利用します。最新の一覧
            および主要なホスティング地域は、<Link to="/security">セキュリティ・委託先</Link>ページにも掲載しています。
          </p>
          <ul>
            <li><strong>Hetzner</strong> — クラウドホスティング・コンピューティング（ドイツ、EU）。</li>
            <li><strong>Cloudflare R2</strong> — アップロード・エクスポート・バックアップ用のオブジェクトストレージ（EU／グローバルエッジ）。</li>
            <li><strong>Stripe</strong> — 決済・サブスクリプション請求。当社はカード番号を保存しません（米国／EU）。</li>
            <li><strong>Amazon SES</strong> — トランザクションメール（EU）。</li>
            <li><strong>OpenRouter</strong> — CadencEZ AI の AI モデルゲートウェイ（米国）。</li>
            <li><strong>Google</strong> — 任意の Google サインイン（米国）。</li>
          </ul>
          <p>
            顧客コンテンツは、既定では欧州連合（ドイツ）にホスティングされます。エンタープライズプランでは、日本国内に
            データを保管するオプションをご利用いただけます。お客様の地域外へデータを移転する場合、当社は標準契約条項
            （SCC）または同等の移転メカニズムに依拠します。
          </p>
        </>
      ),
    },
  },
  {
    id: 'retention',
    en: {
      h: '7. Retention',
      body: (
        <p>We keep account and customer data for as long as your account is active, and for a limited period afterwards as needed to meet legal, accounting, or audit-log obligations. You can request deletion at any time (see below).</p>
      ),
    },
    ja: {
      h: '7. 保存期間',
      body: (
        <p>当社は、お客様のアカウントが有効である限り、アカウント情報および顧客データを保持し、その後も法的・会計・監査ログ上の義務を満たすために必要な限られた期間保持します。お客様はいつでも削除を請求できます（下記参照）。</p>
      ),
    },
  },
  {
    id: 'rights',
    en: {
      h: '8. Your rights (GDPR/UK GDPR)',
      body: (
        <p>
          Depending on where you live, you may have the right to access, correct, delete,
          port, or restrict processing of your personal data, and to object to processing
          or withdraw consent. To exercise any of these, email{' '}
          <a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a>. We respond within
          the timeframe required by applicable law. You also have the right to lodge a
          complaint with your local supervisory authority.
        </p>
      ),
    },
    ja: {
      h: '8. お客様の権利（GDPR／英国 GDPR）',
      body: (
        <p>
          お住まいの地域に応じて、お客様は、ご自身の個人情報へのアクセス、訂正、削除、ポータビリティ、または処理の
          制限を求める権利、ならびに処理に異議を申し立て、または同意を撤回する権利を有する場合があります。これらの
          権利を行使するには、<a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a> までメールでご連絡ください。
          当社は適用される法令で定められた期間内に対応します。また、お客様は所轄の監督機関に苦情を申し立てる権利も
          有します。
        </p>
      ),
    },
  },
  {
    id: 'california',
    en: {
      h: '9. California privacy rights (CCPA/CPRA)',
      body: (
        <>
          <p>
            If you are a California resident, this section describes your rights under the
            CCPA/CPRA and how to exercise them.
          </p>
          <p><strong>Categories of personal information we collect:</strong></p>
          <ul>
            <li>Identifiers (name, work email, account and authentication identifiers, IP address).</li>
            <li>Commercial information (subscription and billing references; we store no card numbers).</li>
            <li>Internet or network activity (usage, device, and audit-log data).</li>
            <li>Professional information (company and role).</li>
            <li>Customer content you choose to upload, which may include the above categories about your own personnel.</li>
          </ul>
          <p>
            <strong>We do not "sell" personal information, and we do not "share" it for
            cross-context behavioral advertising</strong>, as those terms are defined under
            the CCPA/CPRA.
          </p>
          <p><strong>Your rights:</strong></p>
          <ul>
            <li>Right to know and access the personal information we collect about you.</li>
            <li>Right to delete your personal information.</li>
            <li>Right to correct inaccurate personal information.</li>
            <li>Right to opt out of sale or sharing (note: we do neither).</li>
            <li>Right to limit the use of sensitive personal information.</li>
            <li>Right to non-discrimination for exercising your rights.</li>
          </ul>
          <p>
            You may submit a request yourself or through an authorized agent. We will take
            reasonable steps to verify your identity (and an agent's authority) before acting.
            To exercise these rights, email{' '}
            <a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a>.
          </p>
        </>
      ),
    },
    ja: {
      h: '9. カリフォルニア州プライバシー権（CCPA/CPRA）',
      body: (
        <>
          <p>
            お客様がカリフォルニア州の居住者である場合、本セクションは CCPA/CPRA に基づくお客様の権利およびその
            行使方法について説明します。
          </p>
          <p><strong>当社が収集する個人情報のカテゴリー：</strong></p>
          <ul>
            <li>識別子（氏名、業務用メールアドレス、アカウントおよび認証識別子、IP アドレス）。</li>
            <li>商取引情報（サブスクリプションおよび請求の参照情報。カード番号は保存しません）。</li>
            <li>インターネットまたはネットワーク活動情報（利用・デバイス・監査ログデータ）。</li>
            <li>職業に関する情報（会社名および役職）。</li>
            <li>お客様がアップロードを選択した顧客コンテンツ。これにはお客様自身の従業員に関する上記カテゴリーの情報が含まれる場合があります。</li>
          </ul>
          <p>
            当社は、CCPA/CPRA で定義される意味において、個人情報を<strong>「販売（sell）」せず、サイト横断的な
            行動ターゲティング広告のために「共有（share）」しません</strong>。
          </p>
          <p><strong>お客様の権利：</strong></p>
          <ul>
            <li>当社が収集する個人情報を知り、これにアクセスする権利。</li>
            <li>個人情報の削除を求める権利。</li>
            <li>不正確な個人情報の訂正を求める権利。</li>
            <li>販売・共有のオプトアウトを行う権利（当社はいずれも行いません）。</li>
            <li>センシティブな個人情報の利用を制限する権利。</li>
            <li>権利行使を理由とする差別的取扱いを受けない権利。</li>
          </ul>
          <p>
            お客様は、ご自身で、または授権された代理人を通じて請求を行うことができます。当社は、対応に先立ち、
            お客様の本人確認（および代理人の権限確認）のため合理的な措置を講じます。これらの権利を行使するには、
            <a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a> までメールでご連絡ください。
          </p>
        </>
      ),
    },
  },
  {
    id: 'appi',
    en: {
      h: '10. Japan (APPI)',
      body: (
        <>
          <p>
            This section applies to the handling of personal information under Japan's Act
            on the Protection of Personal Information (APPI).
          </p>
          <p>
            <strong>Purpose of use.</strong> We use personal information to provide and secure
            the Service, authenticate users, process billing, respond to support requests,
            comply with legal obligations, and improve the product, as described in this
            policy.
          </p>
          <p>
            <strong>Provision to third parties.</strong> We do not provide personal
            information to third parties except to the subprocessors listed above (which act
            for us in providing the Service), where required by law, or with your consent.
          </p>
          <p>
            <strong>Cross-border transfer.</strong> Customer content is hosted in the EU
            (Germany) by default, with a Japan-resident option available on Enterprise plans.
            Where personal information is transferred to a third party in a foreign country,
            we do so based on appropriate safeguards or your consent, and provide information
            on the relevant measures upon request.
          </p>
          <p>
            <strong>Requests for disclosure, correction, or cessation of use.</strong> You
            may request the disclosure, correction, addition, deletion, or cessation of use
            of your retained personal data, or cessation of its provision to third parties.
            We will respond in accordance with the APPI.
          </p>
          <p>
            <strong>Personal-information handling business operator / contact.</strong>{' '}
            CadencEZ Inc. is the personal-information handling business operator. For APPI
            requests or inquiries, contact{' '}
            <a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a>.
          </p>
        </>
      ),
    },
    ja: {
      h: '10. 日本（個人情報保護法／APPI）',
      body: (
        <>
          <p>
            本セクションは、日本の個人情報の保護に関する法律（個人情報保護法、APPI）に基づく個人情報の取扱いに
            適用されます。
          </p>
          <p>
            <strong>利用目的。</strong>当社は、本ポリシーに記載のとおり、本サービスの提供および安全確保、ユーザー
            認証、請求処理、サポート対応、法的義務の遵守、ならびに製品の改善のために個人情報を利用します。
          </p>
          <p>
            <strong>第三者提供。</strong>当社は、上記に記載した委託先（本サービスの提供のために当社のために行為します）、
            法令で求められる場合、またはお客様の同意がある場合を除き、個人情報を第三者に提供しません。
          </p>
          <p>
            <strong>越境移転。</strong>顧客コンテンツは、既定では EU（ドイツ）にホスティングされ、エンタープライズ
            プランでは日本国内での保管オプションをご利用いただけます。外国にある第三者へ個人情報を移転する場合、
            当社は適切な安全管理措置またはお客様の同意に基づき行い、ご請求に応じて当該措置に関する情報を提供します。
          </p>
          <p>
            <strong>開示・訂正・利用停止等の請求。</strong>お客様は、保有個人データの開示、訂正、追加、削除、利用停止、
            または第三者提供の停止を請求することができます。当社は個人情報保護法に従って対応します。
          </p>
          <p>
            <strong>個人情報取扱事業者・お問い合わせ。</strong>CadencEZ Inc. が個人情報取扱事業者です。個人情報保護法に
            関するご請求・お問い合わせは、<a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a> までご連絡ください。
          </p>
        </>
      ),
    },
  },
  {
    id: 'security',
    en: {
      h: '11. Security',
      body: (
        <p>We protect personal data with encryption in transit (TLS) and encryption at rest for sensitive secrets, role-based access control, per-tenant isolation, and audit logging. See the <Link to="/security">Security</Link> page for detail. No system is perfectly secure, but we work continuously to protect your data.</p>
      ),
    },
    ja: {
      h: '11. セキュリティ',
      body: (
        <p>当社は、転送中の暗号化（TLS）およびセンシティブな秘密情報の保管時暗号化、ロールベースのアクセス制御、テナントごとの分離、ならびに監査ログにより個人情報を保護します。詳細は<Link to="/security">セキュリティ</Link>ページをご覧ください。完全に安全なシステムは存在しませんが、当社は継続的にお客様のデータの保護に努めています。</p>
      ),
    },
  },
  {
    id: 'children',
    en: {
      h: '12. Children',
      body: (
        <p>The Service is a business product not directed to children and is not intended for anyone under 16.</p>
      ),
    },
    ja: {
      h: '12. 子どもについて',
      body: (
        <p>本サービスは事業者向けの製品であり、子どもを対象とするものではなく、16 歳未満の方の利用を想定していません。</p>
      ),
    },
  },
  {
    id: 'changes',
    en: {
      h: '13. Changes & contact',
      body: (
        <p>
          We may update this policy; we will revise the "Last updated" date and, for
          material changes, notify account administrators. Questions or requests:{' '}
          <a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a>. For legal matters,{' '}
          <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>; for security,{' '}
          <a href="mailto:security@cadencez.com">security@cadencez.com</a>.
        </p>
      ),
    },
    ja: {
      h: '13. 変更およびお問い合わせ',
      body: (
        <p>
          当社は本ポリシーを更新することがあります。その場合、「最終更新日」を改訂し、重要な変更については
          アカウント管理者に通知します。ご質問・ご請求は{' '}
          <a href="mailto:privacy@cadencez.com">privacy@cadencez.com</a> まで。法務に関するお問い合わせは{' '}
          <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>、セキュリティに関するお問い合わせは{' '}
          <a href="mailto:security@cadencez.com">security@cadencez.com</a> までご連絡ください。
        </p>
      ),
    },
  },
];

export function PrivacyPage() {
  const isJa = useIsJa();
  return (
    <LegalLayout
      title={isJa ? 'プライバシーポリシー' : 'Privacy Policy'}
      updated="2026-06-03"
      metaTitle={isJa ? 'プライバシーポリシー — CadencEZ' : 'Privacy Policy — CadencEZ'}
      metaDescription={
        isJa
          ? 'CadencEZ が個人情報をどのように収集・利用・保管・保護するか、ならびに GDPR、英国 GDPR、CCPA/CPRA、日本の個人情報保護法（APPI）に基づくお客様の権利について説明します。'
          : "How CadencEZ collects, uses, stores, and protects personal data, and the rights you have under GDPR, the UK GDPR, CCPA/CPRA, and Japan's APPI."
      }
      path="/privacy"
    >
      {SECTIONS.map((s) => {
        const c = isJa ? s.ja : s.en;
        return (
          <LegalSection key={s.id} heading={c.h}>
            {c.body}
          </LegalSection>
        );
      })}
    </LegalLayout>
  );
}
